import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { Tables } from "@how/database";
import { supabase } from "../../lib/supabase";

export type TenantRow = Pick<
  Tables<"tenants">,
  "id" | "slug" | "name" | "status" | "plan" | "created_at"
>;
export type TenantSort = {
  readonly id: "name" | "slug" | "status" | "created_at";
  readonly desc: boolean;
};

export interface TenantsQuery {
  readonly search: string;
  readonly pageIndex: number;
  readonly pageSize: number;
  readonly sort: TenantSort;
}

/** Server-side filtered, sorted, paginated tenant list (RLS: platform members see every tenant). */
export function useTenants(q: TenantsQuery) {
  return useQuery({
    queryKey: ["tenants", q],
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<{ rows: TenantRow[]; total: number }> => {
      const from = q.pageIndex * q.pageSize;
      let query = supabase
        .from("tenants")
        .select("id, slug, name, status, plan, created_at", { count: "exact" })
        .order(q.sort.id, { ascending: !q.sort.desc })
        .range(from, from + q.pageSize - 1);
      if (q.search.trim().length > 0) {
        const term = `%${q.search.trim().replace(/[%_]/g, "")}%`;
        query = query.or(`name.ilike.${term},slug.ilike.${term}`);
      }
      const { data, error, count } = await query;
      if (error) throw error;
      return { rows: data, total: count ?? 0 };
    },
  });
}

export function useTenant(tenantId: string) {
  return useQuery({
    queryKey: ["tenant", tenantId],
    queryFn: async () => {
      const [tenant, domains, entitlements] = await Promise.all([
        supabase.from("tenants").select("*").eq("id", tenantId).single(),
        supabase
          .from("tenant_domains")
          .select("id, hostname, surface, kind, status, is_primary")
          .eq("tenant_id", tenantId)
          .order("surface"),
        supabase
          .from("tenant_module_entitlements")
          .select("module_key, status, starts_at, ends_at, configuration, billing_reference")
          .eq("tenant_id", tenantId)
          .order("module_key"),
      ]);
      if (tenant.error) throw tenant.error;
      if (domains.error) throw domains.error;
      if (entitlements.error) throw entitlements.error;
      return { tenant: tenant.data, domains: domains.data, entitlements: entitlements.data };
    },
  });
}

export function usePlatformStats() {
  return useQuery({
    queryKey: ["platform-stats"],
    queryFn: async () => {
      const [tenants, active, sessions] = await Promise.all([
        supabase.from("tenants").select("id", { count: "exact", head: true }),
        supabase
          .from("tenants")
          .select("id", { count: "exact", head: true })
          .eq("status", "active"),
        supabase
          .from("support_sessions")
          .select("id", { count: "exact", head: true })
          .eq("status", "active"),
      ]);
      for (const r of [tenants, active, sessions]) if (r.error) throw r.error;
      return {
        tenants: tenants.count ?? 0,
        activeTenants: active.count ?? 0,
        activeSessions: sessions.count ?? 0,
      };
    },
  });
}

export function usePlatformActivity() {
  return useQuery({
    queryKey: ["platform-activity"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("audit_events")
        .select("id, occurred_at, action, actor_type, target_table, target_id, tenant_id")
        .order("occurred_at", { ascending: false })
        .limit(10);
      if (error) throw error;
      return data;
    },
  });
}
